import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react";
import {Badge, Button, Card, Col, Container, Form, Row, Spinner, Alert} from "react-bootstrap";
import HeroSection from "../../../components/HeroSection.jsx";
import {AgGridReact} from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {orderApi} from "../../../api/order.js";

function AdminOrdersPage() {
    const gridRef = useRef(null);
    const [gridApi, setGridApi] = useState(null);
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [quickFilter, setQuickFilter] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [taxAuthorityFilter, setTaxAuthorityFilter] = useState("");
    const [customerReferenceFilter, setCustomerReferenceFilter] = useState("");
    const [searchTypeFilter, setSearchTypeFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const columnDefs = useMemo(() => ([
        {headerName: "Order #", field: "orderId", filter: "agNumberColumnFilter", maxWidth: 120},
        {headerName: "Customer", field: "customerName", filter: "agTextColumnFilter"},
        {headerName: "Customer Org", field: "customerOrgName", filter: "agTextColumnFilter"},
        {headerName: "Tax Authority", field: "taxAuthority", filter: "agTextColumnFilter"},
        {headerName: "Customer Ref", field: "customerReference", filter: "agTextColumnFilter"},
        {headerName: "Search Type", field: "searchType", filter: "agTextColumnFilter", maxWidth: 140, valueFormatter: (p) => (p.value ? p.value.toUpperCase() : "-")},
        {headerName: "Search Term", field: "searchTerm", filter: "agTextColumnFilter"},
        {
            headerName: "Order Date",
            field: "orderDate",
            filter: "agDateColumnFilter",
            valueFormatter: (p) => (p.value ? p.value.toLocaleString() : "-"),
            filterParams: {
                comparator: (filterDate, cellValue) => {
                    if (!cellValue) return -1;
                    const cellDate = new Date(cellValue.getFullYear(), cellValue.getMonth(), cellValue.getDate());
                    if (cellDate < filterDate) return -1;
                    if (cellDate > filterDate) return 1;
                    return 0;
                }
            }
        },
        {headerName: "Actions", field: "id", cellRenderer: "actionRenderer", filter: false, sortable: false, maxWidth: 120}
    ]), []);

    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 140,
        sortable: true,
        filter: true,
        floatingFilter: true,
        resizable: true
    }), []);

    const components = useMemo(() => ({
        actionRenderer: ActionRenderer
    }), []);

    const loadOrders = useCallback(async () => {
        setIsLoading(true);
        try {
            const orders = await orderApi.getOrders();
            const normalizedOrders = orders.map((order) => ({
                id: order.orderId,
                orderId: order.orderId,
                customerName: order.customerName,
                customerOrgName: order.customerOrgName,
                taxAuthority: order.taxAuthority,
                customerReference: order.customerReference,
                searchType: order.searchType ? order.searchType.toLowerCase() : "",
                searchTerm: order.searchTerm,
                orderDate: order.orderDate ? new Date(order.orderDate) : null
            }));
            setRowData(normalizedOrders);
            setError("");
        } catch (loadError) {
            console.error("Failed to load admin orders", loadError);
            setError("Failed to load orders. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    useEffect(() => {
        if (!gridApi) return;
        gridApi.setQuickFilter(quickFilter);
        const managed = ["customerName", "customerOrgName", "taxAuthority", "customerReference", "searchType", "orderDate"];
        const nextModel = {...(gridApi.getFilterModel() || {})};
        managed.forEach((c) => delete nextModel[c]);

        if (customerFilter) nextModel.customerName = {filterType: "text", type: "contains", filter: customerFilter};
        if (taxAuthorityFilter) nextModel.taxAuthority = {filterType: "text", type: "contains", filter: taxAuthorityFilter};
        if (customerReferenceFilter) nextModel.customerReference = {filterType: "text", type: "contains", filter: customerReferenceFilter};
        if (searchTypeFilter) nextModel.searchType = {filterType: "text", type: "equals", filter: searchTypeFilter};

        if (dateFrom || dateTo) {
            let dateModel = null;
            if (dateFrom && dateTo) dateModel = {filterType: "date", type: "inRange", dateFrom, dateTo};
            else if (dateFrom) dateModel = {filterType: "date", type: "greaterThan", dateFrom};
            else if (dateTo) dateModel = {filterType: "date", type: "lessThan", dateFrom: dateTo};
            if (dateModel) nextModel.orderDate = dateModel;
        }
        gridApi.setFilterModel(nextModel);
        gridApi.onFilterChanged();
    }, [gridApi, quickFilter, customerFilter, taxAuthorityFilter, customerReferenceFilter, searchTypeFilter, dateFrom, dateTo]);

    const handleClearFilters = () => {
        setQuickFilter("");
        setCustomerFilter("");
        setTaxAuthorityFilter("");
        setCustomerReferenceFilter("");
        setSearchTypeFilter("");
        setDateFrom("");
        setDateTo("");
        if (gridApi) {
            gridApi.setFilterModel({});
            gridApi.setQuickFilter("");
            gridApi.onFilterChanged();
        }
    };

    const onGridReady = useCallback((params) => {
        setGridApi(params.api);
    }, []);

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="admin"
                eyebrow="Admin"
                title="Orders Overview"
                subtitle="Review all orders across customers and municipalities."
                actions={<Button variant="primary" href="/admin/reports">Open Reports</Button>}
            />
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            <Card className="dashboard-panel mt-4">
                <Card.Header className="bg-white"><strong>Filters</strong></Card.Header>
                <Card.Body>
                    <Form>
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Form.Label>Quick Search</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={quickFilter}
                                    placeholder="Search across all columns"
                                    onChange={(e) => setQuickFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Customer</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerFilter}
                                    placeholder="Filter by customer"
                                    onChange={(e) => setCustomerFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Tax Authority</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={taxAuthorityFilter}
                                    placeholder="Filter by tax authority"
                                    onChange={(e) => setTaxAuthorityFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Customer Ref</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerReferenceFilter}
                                    placeholder="Filter by reference"
                                    onChange={(e) => setCustomerReferenceFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Search Type</Form.Label>
                                <Form.Select
                                    value={searchTypeFilter}
                                    onChange={(e) => setSearchTypeFilter(e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="pid">PID</option>
                                    <option value="roll">Roll</option>
                                    <option value="mhr">MHR</option>
                                    <option value="address">Address</option>
                                </Form.Select>
                            </Col>
                            <Col md={2}>
                                <Form.Label>Date From</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Date To</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </Col>
                            <Col md="auto">
                                <Button variant="outline-secondary" onClick={handleClearFilters}>
                                    Clear Filters
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
            <Card className="dashboard-panel mt-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong>All Orders</strong>
                    <Badge bg="light" text="dark">Admin</Badge>
                </Card.Header>
                <Card.Body>
                    {isLoading ? (
                        <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span>Loading orders...</span>
                        </div>
                    ) : (
                        <div className="ag-theme-alpine dashboard-grid" style={{height: "600px", width: "100%"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                components={components}
                                pagination={true}
                                paginationAutoPageSize={true}
                                animateRows={true}
                                onGridReady={onGridReady}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    )
}

function ActionRenderer(props) {
    return (
        <div style={{width: "100%", textAlign: "center"}}>
            <a href={`/admin/order/${props.data.id}`}><i style={{fontSize: 18}} className="bi bi-file-pdf"></i></a>&nbsp;
            <a href={`/admin/order/${props.data.id}`}><i style={{fontSize: 18}} className="bi bi-search"></i></a>
        </div>
    );
}

export default observer(AdminOrdersPage);
